import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Capacitor } from "@capacitor/core";
import type {
  DocxAnnotations,
  DocxPageSize,
  LocalEditPatch,
  NevisignDocumentListItem,
  ReaderVersionManifest,
} from "@nevisign/reader-core";
import {
  createNevisignDocument,
  extractDocxAnnotations,
  fetchNevisignDocumentExport,
  nevisignBodyToReaderHtml,
  OFFICE_DOCUMENT_TEXT_MAX,
  renderDocxToHtml,
} from "@nevisign/reader-core";
import {
  ArrowRight,
  Cloud,
  FileUp,
  FolderOpen,
  LayoutDashboard,
  Menu,
  MessagesSquare,
  RefreshCw,
  Save,
  ScrollText,
  Settings2,
  ShieldCheck,
  X,
} from "lucide-react";
import { DocxViewer, usePinchZoom } from "./components/DocxViewer";
import {
  clampZoom,
  getCanvasScroll,
  zoomAtViewportCenter,
} from "./lib/pinchZoom";
import {
  FloatingSelectionToolbar,
  useSelectionToolbar,
} from "./components/FloatingSelectionToolbar";
import { CommentsSidebar } from "./components/CommentsSidebar";
import { CommentFocusSheet } from "./components/CommentFocusSheet";
import { AddCommentSheet } from "./components/AddCommentSheet";
import { SettingsPanel } from "./components/SettingsPanel";
import { NevisignDocsPanel } from "./components/NevisignDocsPanel";
import {
  AssistantFab,
  AssistantSheet,
} from "./components/AssistantSheet";
import {
  ReaderToolbar,
  toPersianDigits,
} from "./components/ReaderToolbar";
import { LogsPanel } from "./components/LogsPanel";
import { ChatListPage } from "./components/ChatListPage";
import { PrintSheet } from "./components/PrintSheet";
import { UpdateBanner } from "./components/UpdateBanner";
import { PdfReportSheet } from "./components/PdfReportSheet";
import { PdfViewer, type PdfViewerHandle } from "./components/PdfViewer";
import { ToolsMenu } from "./components/ToolsMenu";
import { HomeDashboard, type KartablOpenPayload } from "./components/HomeDashboard";
import { CaseDetailPage } from "./components/CaseDetailPage";
import { CasesListPage } from "./components/CasesListPage";
import { AppAuthChip, ProfileSheet } from "./components/AppAuthChip";
import { ApprovalActSheet } from "./components/ApprovalActSheet";
import { ContractDraftHub } from "./components/ContractDraftHub";
import {
  SimpleDocSaveModal,
  type SimpleDocSaveDestination,
} from "./components/SimpleDocSaveModal";
import {
  SignatureActionSheet,
  type SignatureActionResult,
  type SignatureActionSigner,
} from "./components/SignatureActionSheet";
import { ContractAiCreatePage } from "./components/ContractAiCreatePage";
import { DocumentUploadPage } from "./components/DocumentUploadPage";
import { ScanDocumentPage } from "./components/ScanDocumentPage";
import { SignStartPage } from "./components/SignStartPage";
import { DadnoHubPage } from "./components/DadnoHubPage";
import { DadnoChatPage } from "./components/DadnoChatPage";
import type { LocalDraft } from "./lib/local-drafts";
import {
  buildSimpleDocxBytes,
  DOCX_MIME,
  extractLiveDocxHtml,
} from "./lib/simpleDocx";
import type { ReaderInboxTask } from "@nevisign/reader-core";
import {
  LegalRiskToolPage,
  MinimizedToolChip,
  RevToast,
  SummaryToolPage,
} from "./components/ToolPages";
import { PdfMergeSheet } from "./components/PdfToolSheets";
import { PdfSplitPage } from "./components/PdfSplitPage";
import { ToImageSheet } from "./components/ToImageSheet";
import { TextComparePage } from "./components/TextComparePage";
import { PageHud, PageScrollbar } from "./components/PageScrollbar";
import { defaultPdfTextLayerEnabled } from "./lib/pdfAssets";
import { useSettings } from "./hooks/useSettings";
import { detectKind, type OpenedFile } from "./lib/file";
import { useCommentActions } from "./hooks/useCommentActions";
import {
  wireIncomingFileOpener,
} from "./lib/incomingFile";
import {
  loadLocalComments,
  mergeComments,
  saveLocalComments,
  type PdfCommentAnchor,
  type ReaderComment,
} from "./lib/annotationStore";
import {
  getOrCreateChatForFile,
  listChatThreads,
  updateChatFileMeta,
  updateChatMessages,
  type FileChatThread,
} from "./lib/chatStore";
import { fetchCloudChat } from "./lib/cloudChat";
import { detectPendingSignature } from "./lib/signing/detect-pending-sign";
import type { ReaderSigningSession } from "./lib/signing/types";
import { SigningOverlay } from "./components/signing/SigningOverlay";
import { EsignOnboardFlow } from "./components/esign/EsignOnboardFlow";
import {
  fetchEsignProfile,
} from "./lib/esign/api";
import {
  loadEsignLocal,
  saveEsignLocal,
} from "./lib/esign/local-store";
import { extractLiveDocxText } from "./lib/applyOfficeEdits";
import {
  appendLog,
  shareOpenDocument,
  type LogFileExport,
} from "./lib/logger";
import { printOpenDocument } from "./lib/print";
import {
  APP_VERSION_CODE,
  APP_VERSION_NAME,
  NEVISIGN_SITE_URL,
} from "./lib/constants";
import {
  checkForUpdate,
  downloadAndInstallApk,
  getNativeVersion,
} from "./lib/update";
import {
  cacheOpenedDocument,
  resolveDocumentForThread,
} from "./lib/documentCache";
import {
  analyzeLegalRisk,
  READER_TOOLS,
  summarizeDocument,
  type LegalRiskResult,
  type ToolId,
} from "./lib/readerTools";
import { legalRiskToMarkdown } from "./lib/markdown";
import { saveAndShareBytes, shareText } from "./lib/shareText";
import { useAuth } from "./contexts/auth-context";
import { handleApiAuthError } from "./lib/auth-required";
import {
  createContractFromUpload,
  startContractSignature,
} from "./lib/platform-contracts";

type SideMode =
  | "none"
  | "comments"
  | "settings"
  | "nevisign"
  | "logs"
  | "chats";

type HomeView =
  | "dashboard"
  | "sign-start"
  | "contract-hub"
  | "contract-ai"
  | "contract-upload"
  | "contract-scan"
  | "dadno-hub"
  | "dadno-chat"
  | "nevisign-docs"
  | "chat-list"
  | "cases-list"
  | "settings"
  | "esign-onboard";

/** Drawer footer: «نسخه ۱.۲۰» — numeric core only, Persian digits. */
function formatDrawerVersion(versionName: string): string {
  const simple = versionName.match(/^\d+(?:\.\d+)*/)?.[0] ?? versionName;
  return `نسخه ${toPersianDigits(simple)}`;
}

function siteHostLabel(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

const DEFAULT_PAGE: DocxPageSize = {
  widthTwip: 11906,
  heightTwip: 16838,
  widthPx: 11906 / 15,
  heightPx: 16838 / 15,
  marginTopPx: 96,
  marginRightPx: 96,
  marginBottomPx: 96,
  marginLeftPx: 96,
};

/** Empty editable page — same DocxViewer chrome as an opened Word file. */
const BLANK_SIMPLE_DOCX_HTML = `<p class="nr-p" dir="rtl"><br></p>`;

function readerHtmlForSimpleDraft(draft?: {
  html?: string;
  text?: string;
} | null): string {
  const html = draft?.html?.trim();
  if (html) return html;
  const text = draft?.text?.trim();
  if (text) return nevisignBodyToReaderHtml(text, { dir: "rtl" });
  return BLANK_SIMPLE_DOCX_HTML;
}

function simpleDocFileName(title?: string): string {
  const base = (title ?? "").trim() || "سند بدون عنوان";
  return base.toLowerCase().endsWith(".docx") ? base : `${base}.docx`;
}

export function App() {
  const { settings, updateSettings } = useSettings();
  const { requireAuth, isAuthenticated, user } = useAuth();
  const [file, setFile] = useState<OpenedFile | null>(null);
  const [activeCaseId, setActiveCaseId] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string>("");
  const [docxPageSize, setDocxPageSize] =
    useState<DocxPageSize>(DEFAULT_PAGE);
  const [annotations, setAnnotations] = useState<DocxAnnotations | null>(null);
  const [readerComments, setReaderComments] = useState<ReaderComment[]>([]);
  const [patches, setPatches] = useState<LocalEditPatch[]>([]);
  const [side, setSide] = useState<SideMode>("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [focusCommentId, setFocusCommentId] = useState<string | null>(null);
  const [addCommentQuote, setAddCommentQuote] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantFull, setAssistantFull] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [chatList, setChatList] = useState<FileChatThread[]>([]);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [docxPage, setDocxPage] = useState(1);
  const [docxPageCount, setDocxPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pendingQuote, setPendingQuote] = useState<string | null>(null);
  /** Selection scope for AI apply — only when user sent a highlight */
  const [assistantScopedSelection, setAssistantScopedSelection] = useState<
    string | null
  >(null);
  const [fileMissingHint, setFileMissingHint] = useState<string | null>(null);
  const [openingNevisignId, setOpeningNevisignId] = useState<string | null>(
    null
  );
  const [printOpen, setPrintOpen] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [cloudSaving, setCloudSaving] = useState(false);
  const [sharingDoc, setSharingDoc] = useState(false);
  const [versionLabel, setVersionLabel] = useState(
    `${APP_VERSION_NAME} (${APP_VERSION_CODE})`
  );
  const [drawerVersionName, setDrawerVersionName] = useState(APP_VERSION_NAME);
  const [updateManifest, setUpdateManifest] =
    useState<ReaderVersionManifest | null>(null);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateProgress, setUpdateProgress] = useState<number | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateCheckBusy, setUpdateCheckBusy] = useState(false);
  const [updateToast, setUpdateToast] = useState<string | null>(null);
  const [pdfTextLayer, setPdfTextLayer] = useState(defaultPdfTextLayerEnabled);
  const [pdfExtractedText, setPdfExtractedText] = useState("");
  const [pdfReportExport, setPdfReportExport] = useState<LogFileExport | null>(
    null
  );
  const [pdfReportNativeVer, setPdfReportNativeVer] = useState<{
    versionCode: number;
    versionName: string;
  } | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [activeTool, setActiveTool] = useState<ToolId | null>(null);
  const [minimizedTool, setMinimizedTool] = useState<ToolId | null>(null);
  const [pendingHomeTool, setPendingHomeTool] = useState<ToolId | null>(null);
  const [summaryText, setSummaryText] = useState("");
  const [summaryHistory, setSummaryHistory] = useState<string[]>([]);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryRegenerating, setSummaryRegenerating] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [riskResult, setRiskResult] = useState<LegalRiskResult | null>(null);
  const [riskBusy, setRiskBusy] = useState(false);
  const [riskError, setRiskError] = useState<string | null>(null);
  const [revToast, setRevToast] = useState<string | null>(null);
  const [signingSession, setSigningSession] =
    useState<ReaderSigningSession | null>(null);
  const [signingSheetOpen, setSigningSheetOpen] = useState(false);
  const [signatureActionOpen, setSignatureActionOpen] = useState(false);
  const [signatureActionBusy, setSignatureActionBusy] = useState(false);
  const [signatureActionError, setSignatureActionError] = useState<string | null>(null);
  const [signatureActionResult, setSignatureActionResult] =
    useState<SignatureActionResult | null>(null);
  const [homeView, setHomeView] = useState<HomeView>("dashboard");
  const [dashboardScrolled, setDashboardScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [esignBannerVisible, setEsignBannerVisible] = useState(false);
  const [kartablTask, setKartablTask] = useState<ReaderInboxTask | null>(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [pendingDashboardSign, setPendingDashboardSign] = useState(false);
  const [dadnoSeed, setDadnoSeed] = useState<{
    message: string;
    attachmentName?: string;
    mode?: "summary" | "risk" | "consult";
    returnTo?: "dashboard" | "dadno-hub";
  } | null>(null);
  /** Blank «محیط نگارش ساده» flow — scopes header chrome (back / no logo / save). */
  const [blankSimpleMode, setBlankSimpleMode] = useState(false);
  const [blankSimpleReturnView, setBlankSimpleReturnView] =
    useState<HomeView>("contract-hub");
  const [simpleSaveOpen, setSimpleSaveOpen] = useState(false);
  const [simpleSaveBusy, setSimpleSaveBusy] = useState(false);
  const [simpleSaveError, setSimpleSaveError] = useState<string | null>(null);
  const [scanInitialDocId, setScanInitialDocId] = useState<string | null>(null);
  const [scanStartLibrary, setScanStartLibrary] = useState(false);
  const pendingPdfAnchorRef = useRef<PdfCommentAnchor | null>(null);

  const docxContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const pdfRef = useRef<PdfViewerHandle>(null);

  const viewingComment = Boolean(focusCommentId);
  const addingComment = addCommentQuote !== null;
  const selectionRootRef =
    file?.kind === "pdf" ? pdfContainerRef : docxContainerRef;
  const selectionEnabled =
    Boolean(file) &&
    (file?.kind === "docx" || file?.kind === "pdf") &&
    !viewingComment &&
    !addingComment;
  const { state: selectionState, hide: hideSelection, suppressBriefly } =
    useSelectionToolbar(selectionRootRef, selectionEnabled);

  usePinchZoom(Boolean(file), zoom, setZoom);

  // Drop stale focus if the active comment was deleted out-of-band
  useEffect(() => {
    if (!focusCommentId) return;
    const alive = readerComments.some(
      (c) => !c.deleted && c.word_id === focusCommentId
    );
    if (!alive) {
      setFocusCommentId(null);
      setActiveCommentId((prev) => (prev === focusCommentId ? null : prev));
    }
  }, [focusCommentId, readerComments]);

  const persistComments = useCallback(
    (next: ReaderComment[]) => {
      setReaderComments(next);
      if (file) saveLocalComments(file.name, next);
      setAnnotations((prev) =>
        prev
          ? {
              ...prev,
              comments: next.filter((c) => !c.deleted),
              counts: {
                ...prev.counts,
                comments: next.filter((c) => !c.deleted).length,
              },
            }
          : prev
      );
    },
    [file]
  );

  const openBuffer = useCallback(
    async (
      name: string,
      buffer: ArrayBuffer,
      mimeType?: string,
      fileUri?: string
    ) => {
      setBlankSimpleMode(false);
      setSimpleSaveOpen(false);
      setSimpleSaveError(null);
      setBusy(true);
      setError(null);
      setActiveCaseId(null);
      setSigningSession(null);
      setSigningSheetOpen(false);
      setPatches([]);
      setActiveCommentId(null);
      setFocusCommentId(null);
      setAddCommentQuote(null);
      setSide("none");
      setMenuOpen(false);
      setPdfPage(1);
      setPdfPageCount(0);
      setDocxPage(1);
      setDocxPageCount(0);
      setZoom(1);
      setPdfExtractedText("");
      // Android: canvas-only by default (Persian shaping). Never force text layer on.
      setPdfTextLayer(defaultPdfTextLayerEnabled());

      const kind = detectKind(name, mimeType, buffer);
      try {
        // Durable local copy so chats can reopen after content:// grants expire
        const cachePath = await cacheOpenedDocument(name, buffer, {
          mimeType,
          sourceUri: fileUri,
        });

        if (kind === "docx") {
          const [htmlResult, ann] = await Promise.all([
            renderDocxToHtml(buffer),
            extractDocxAnnotations(buffer),
          ]);
          const local = loadLocalComments(name);
          const merged = mergeComments(ann.comments, local);
          setDocxHtml(htmlResult.html);
          setDocxPageSize(htmlResult.pageSize ?? DEFAULT_PAGE);
          setAnnotations({
            ...ann,
            comments: merged,
            counts: { ...ann.counts, comments: merged.length },
          });
          setReaderComments(merged);
          saveLocalComments(name, merged);
          setFile({ name, kind, buffer, mimeType, fileUri });
          const chat = getOrCreateChatForFile(name, buffer.byteLength, {
            fileUri,
            filePath: cachePath,
            mimeType,
            byteLength: buffer.byteLength,
          });
          setActiveChatId(chat.id);
          setChatList(listChatThreads());
          appendLog("info", "file", `opened docx ${name}`, {
            bytes: buffer.byteLength,
            fileUri,
            cachePath,
          });
        } else if (kind === "pdf") {
          setDocxHtml("");
          setAnnotations(null);
          const local = loadLocalComments(name);
          setReaderComments(local);
          const safeName = name.toLowerCase().endsWith(".pdf")
            ? name
            : `${name.replace(/\.[^.]+$/, "") || name}.pdf`;
          setFile({
            name: safeName,
            kind: "pdf",
            buffer,
            mimeType,
            fileUri,
          });
          const chat = getOrCreateChatForFile(safeName, buffer.byteLength, {
            fileUri,
            filePath: cachePath,
            mimeType,
            byteLength: buffer.byteLength,
          });
          setActiveChatId(chat.id);
          setChatList(listChatThreads());
          appendLog("info", "file", `opened pdf ${safeName}`, {
            bytes: buffer.byteLength,
            fileUri,
            cachePath,
          });
        } else if (kind === "doc") {
          setError(
            "فرمت قدیمی Word (.doc) پشتیبانی نمی‌شود. فایل را به DOCX تبدیل کنید."
          );
          setFile(null);
        } else {
          setError("فقط فایل‌های PDF و DOCX پشتیبانی می‌شوند.");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "خطا در باز کردن فایل");
        setFile(null);
        appendLog("error", "file", "open failed", err);
      } finally {
        setBusy(false);
      }
    },
    []
  );

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    void wireIncomingFileOpener(openBuffer).then((fn) => {
      cleanup = fn;
    });
    return () => cleanup?.();
  }, [openBuffer]);

  useEffect(() => {
    // Backdrop handles outside-click; avoid mousedown closing before
    // "باز کردن فایل" can trigger the file input in the same gesture.
  }, [menuOpen]);

  useEffect(() => {
    void getNativeVersion().then((v) => {
      setVersionLabel(`${v.versionName} (${v.versionCode})`);
      setDrawerVersionName(v.versionName);
    });
  }, []);

  useEffect(() => {
    // OTA APK updates are Android-only; skip on web so the demo stays clean.
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await checkForUpdate();
        if (cancelled) return;
        if (result.updateAvailable) {
          setUpdateManifest(result.manifest);
          setForceUpdate(result.forceUpdate);
        }
      } catch (err) {
        appendLog("warn", "update", "check failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Extract PDF text for assistant after load
  useEffect(() => {
    if (!file || file.kind !== "pdf") return;
    const t = window.setTimeout(() => {
      void pdfRef.current?.getDiagnostics().then((d) => {
        setPdfExtractedText(d.samplePage1Text || "");
      });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [file]);

  useEffect(() => {
    if (side === "chats") setChatList(listChatThreads());
  }, [side]);

  useEffect(() => {
    const scroller = document.querySelector(".canvas-scroll") as HTMLElement | null;
    if (!scroller) {
      setDashboardScrolled(false);
      return;
    }
    const update = () => setDashboardScrolled(scroller.scrollTop > 8);
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    return () => scroller.removeEventListener("scroll", update);
  }, [activeCaseId, error, file, homeView]);

  const onPickFile = useCallback(
    async (list: FileList | null) => {
      const f = list?.[0];
      if (!f) return;
      const buffer = await f.arrayBuffer();
      await openBuffer(f.name, buffer, f.type || undefined);
    },
    [openBuffer]
  );

  const toggleSide = (mode: SideMode) => {
    if (mode === "nevisign") {
      requireAuth(() => {
        setSide((s) => (s === "nevisign" ? "none" : "nevisign"));
        setMenuOpen(false);
      });
      return;
    }
    setSide((s) => (s === mode ? "none" : mode));
    setMenuOpen(false);
  };

  const applyLocalEdit = useCallback(
    (
      partial: Omit<LocalEditPatch, "id" | "appliedAt" | "selectionText"> & {
        selectionText: string;
      }
    ) => {
      const patch: LocalEditPatch = {
        id: crypto.randomUUID(),
        selectionText: partial.selectionText,
        fontFamily: partial.fontFamily,
        fontColor: partial.fontColor,
        textAlign: partial.textAlign,
        appliedAt: new Date().toISOString(),
      };
      setPatches((prev) => [...prev, patch]);

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
      const range = sel.getRangeAt(0);
      const span = document.createElement("span");
      span.className = "nr-local-edit";
      span.dataset.patchId = patch.id;
      if (partial.fontFamily) span.style.fontFamily = partial.fontFamily;
      if (partial.fontColor) span.style.color = partial.fontColor;
      try {
        range.surroundContents(span);
      } catch {
        const node = sel.anchorNode?.parentElement?.closest(".nr-p") as
          | HTMLElement
          | null;
        if (node && partial.textAlign) {
          node.style.textAlign = partial.textAlign;
        }
        if (node && partial.fontFamily) node.style.fontFamily = partial.fontFamily;
        if (node && partial.fontColor) node.style.color = partial.fontColor;
      }

      if (partial.textAlign) {
        const p = span.closest(".nr-p") as HTMLElement | null;
        if (p) p.style.textAlign = partial.textAlign;
      }

      try {
        const key = file ? `nr-patches:${file.name}` : null;
        if (key) {
          const next = [...patches, patch];
          localStorage.setItem(key, JSON.stringify(next));
        }
      } catch {
        /* ignore quota */
      }
    },
    [file, patches]
  );

  const execInline = useCallback((command: string) => {
    try {
      document.execCommand(command);
    } catch {
      /* ignore */
    }
  }, []);

  const beginAddComment = useCallback((forcedQuote?: string) => {
    const quote =
      (forcedQuote?.trim() || window.getSelection()?.toString().trim()) ?? "";
    if (!quote) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRangeRef.current = null;
    }
    setAddCommentQuote(quote);
    hideSelection();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [hideSelection]);

  const beginPdfLongPressComment = useCallback(
    (info: {
      page: number;
      quote: string;
      xNorm: number;
      yNorm: number;
    }) => {
      setPdfTextLayer(true);
      pendingPdfAnchorRef.current = {
        page: info.page,
        xNorm: info.xNorm,
        yNorm: info.yNorm,
      };
      const q =
        info.quote.trim() ||
        `یادداشت روی صفحه ${info.page.toLocaleString("fa-IR")}`;
      beginAddComment(q);
    },
    [beginAddComment]
  );

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setEsignBannerVisible(false);
      return;
    }
    let cancelled = false;
    const local = loadEsignLocal(user.id);
    if (local?.onboardingComplete || local?.identityVerified) {
      setEsignBannerVisible(false);
      return;
    }
    if (local?.bannerDismissedAt) {
      setEsignBannerVisible(false);
    } else {
      setEsignBannerVisible(true);
    }
    void fetchEsignProfile(settings.nevisignApiBase)
      .then((p) => {
        if (cancelled) return;
        if (p.onboardingComplete || p.identityVerified) {
          setEsignBannerVisible(false);
        } else if (!loadEsignLocal(user.id)?.bannerDismissedAt) {
          setEsignBannerVisible(true);
        }
      })
      .catch(() => {
        /* keep local banner decision */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, settings.nevisignApiBase]);

  const sendSelectionToAssistant = useCallback(() => {
    const quote = selectionState.quote.trim();
    if (!quote) return;
    requireAuth(() => {
      setPendingQuote(quote);
      setAssistantScopedSelection(quote);
      setAssistantOpen(true);
      setAssistantFull(false);
      hideSelection();
    });
  }, [hideSelection, requireAuth, selectionState.quote]);

  const openNevisignDocument = useCallback(
    async (doc: NevisignDocumentListItem) => {
      if (!isAuthenticated) {
        requireAuth(() => void openNevisignDocument(doc));
        return;
      }
      setBlankSimpleMode(false);
      setSimpleSaveOpen(false);
      setOpeningNevisignId(doc.id);
      setError(null);
      setBusy(true);
      try {
        const exported = await fetchNevisignDocumentExport(
          settings.nevisignApiBase,
          doc.id
        );
        const body = (exported.body_cm || exported.body_text || "").trim();
        const title = exported.title || doc.title || `سند ${doc.id}`;
        const html = nevisignBodyToReaderHtml(body, { title, dir: "rtl" });
        const name = title.endsWith(".docx") ? title : `${title}.docx`;
        // Synthetic empty buffer — cloud text view, not a real OOXML file
        const buffer = new ArrayBuffer(0);
        setDocxHtml(html);
        setDocxPageSize(DEFAULT_PAGE);
        setAnnotations({
          comments: [],
          revisions: [],
          finalText: body,
          originalText: body,
          counts: { comments: 0, insertions: 0, deletions: 0 },
        });
        const local = loadLocalComments(name);
        setReaderComments(local);
        setFile({
          name,
          kind: "docx",
          buffer,
          mimeType: "text/plain",
          fileUri: undefined,
          nevisignDocumentId: doc.id,
        });
        setPatches([]);
        setActiveCommentId(null);
        setFocusCommentId(null);
        setSide("none");
        setMenuOpen(false);
        setActiveCaseId(null);
        setZoom(1);
        setFileMissingHint(null);
        setHomeView("dashboard");
        const chat = getOrCreateChatForFile(name, buffer.byteLength, {
          mimeType: "text/plain",
          byteLength: 0,
          nevisignDocumentId: doc.id,
        });
        // Pull unified studio chat history
        void fetchCloudChat(settings.nevisignApiBase, doc.id).then((msgs) => {
          if (msgs && msgs.length) {
            updateChatMessages(chat.id, msgs);
            updateChatFileMeta(chat.id, { nevisignDocumentId: doc.id });
          }
        });
        setActiveChatId(chat.id);
        setChatList(listChatThreads());
        appendLog("info", "nevisign", `opened cloud doc ${doc.id}`, {
          title,
          chars: body.length,
        });
        const pending = await detectPendingSignature(
          settings.nevisignApiBase,
          doc.id,
        );
        setSigningSession(pending);
        setSigningSheetOpen(Boolean(pending?.canAct));
      } catch (err) {
        if (
          handleApiAuthError(err, () => {
            void openNevisignDocument(doc);
          })
        ) {
          return;
        }
        const msg =
          err instanceof Error ? err.message : "باز کردن سند نویساین ناموفق بود";
        setError(msg);
        appendLog("error", "nevisign", msg);
      } finally {
        setBusy(false);
        setOpeningNevisignId(null);
      }
    },
    [isAuthenticated, requireAuth, settings.nevisignApiBase]
  );

  /** Open the real Word/DOCX reader surface with a blank (or draft) editable page. */
  const openBlankSimpleDocument = useCallback(
    (
      seed?: { title?: string; html?: string; text?: string } | null,
      opts?: { returnView?: HomeView },
    ) => {
      const title = seed?.title?.trim() || "سند بدون عنوان";
      const name = simpleDocFileName(title);
      const html = readerHtmlForSimpleDraft(seed);
      const buffer = new ArrayBuffer(0);
      setError(null);
      setBlankSimpleMode(true);
      setBlankSimpleReturnView(opts?.returnView ?? "contract-hub");
      setSimpleSaveOpen(false);
      setSimpleSaveError(null);
      setDocxHtml(html);
      setDocxPageSize(DEFAULT_PAGE);
      setAnnotations({
        comments: [],
        revisions: [],
        finalText: seed?.text?.trim() || "",
        originalText: seed?.text?.trim() || "",
        counts: { comments: 0, insertions: 0, deletions: 0 },
      });
      const local = loadLocalComments(name);
      setReaderComments(local);
      setFile({
        name,
        kind: "docx",
        buffer,
        mimeType: DOCX_MIME,
        fileUri: undefined,
      });
      setPatches([]);
      setActiveCommentId(null);
      setFocusCommentId(null);
      setSide("none");
      setMenuOpen(false);
      setActiveCaseId(null);
      setKartablTask(null);
      setZoom(1);
      setFileMissingHint(null);
      setHomeView("dashboard");
      setSigningSession(null);
      setSigningSheetOpen(false);
      const chat = getOrCreateChatForFile(name, buffer.byteLength, {
        mimeType: DOCX_MIME,
        byteLength: 0,
      });
      setActiveChatId(chat.id);
      setChatList(listChatThreads());
      appendLog("info", "file", `opened blank simple doc ${name}`);
      // Wait for DocxViewer to mount/inject before focusing the page.
      window.setTimeout(() => {
        const root = docxContainerRef.current;
        const editable =
          (root?.querySelector(
            '.docx-editable[contenteditable="true"]',
          ) as HTMLElement | null) ??
          (root?.querySelector(
            '[contenteditable="true"]',
          ) as HTMLElement | null) ??
          root;
        if (!editable) return;
        editable.focus();
        const sel = window.getSelection();
        if (!sel) return;
        const range = document.createRange();
        range.selectNodeContents(editable);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }, 80);
    },
    [],
  );

  const {
    submitAddComment, replyToComment, unwrapCommentMarks, deleteComment, deleteReply, resolveComment, resolveAllComments, deleteAllComments, editComment, editReply,
  } = useCommentActions({
    file, docxHtml, readerComments, activeCommentId, setActiveCommentId, focusCommentId, setFocusCommentId, addCommentQuote, setAddCommentQuote, pendingPdfAnchorRef, docxContainerRef, pdfContainerRef, savedRangeRef, hideSelection, persistComments,
  });

  useEffect(() => {
    if (!file || file.kind !== "docx") return;
    try {
      const raw = localStorage.getItem(`nr-patches:${file.name}`);
      if (raw) setPatches(JSON.parse(raw) as LocalEditPatch[]);
    } catch {
      /* ignore */
    }
  }, [file]);

  const sideTitle = useMemo(() => {
    if (side === "comments") return "یادداشت‌ها";
    if (side === "settings") return "تنظیمات";
    if (side === "nevisign") return "اسناد نویساین";
    if (side === "logs") return "لاگ‌ها";
    if (side === "chats") return "گفتگوها";
    return "";
  }, [side]);

  const topTitle = useMemo(() => {
    if (busy) return "در حال بارگذاری…";
    if (file) return file.name;
    if (activeCaseId) return "جزئیات پرونده";
    if (homeView === "sign-start") return "امضای اسناد";
    if (homeView === "contract-hub") return "شروع قرارداد";
    if (homeView === "contract-ai") return "تدوین با هوش مصنوعی";
    if (homeView === "contract-upload") return "بارگذاری سند";
    if (homeView === "contract-scan") return "اسکن سند";
    if (homeView === "dadno-hub") return "هاب دادنو۷";
    if (homeView === "dadno-chat") {
      return dadnoSeed?.mode === "consult" ? "مشاوره هوشمند" : "گفتگوی دادنو۷";
    }
    if (homeView === "nevisign-docs") return "اسناد نویساین";
    if (homeView === "chat-list") return "گفتگوها";
    if (homeView === "cases-list") return "پرونده‌ها";
    if (homeView === "settings") return "تنظیمات";
    return "";
  }, [activeCaseId, busy, dadnoSeed?.mode, file, homeView]);

  const openFilePicker = () => {
    // Keep menu open until after the click() so Android/WebView still
    // treats it as a user gesture for the file input.
    const input = fileInputRef.current;
    input?.click();
    window.setTimeout(() => setMenuOpen(false), 0);
  };

  const goHome = useCallback(() => {
    setFile(null);
    setActiveCaseId(null);
    setError(null);
    setMenuOpen(false);
    setSide("none");
    setAssistantOpen(false);
    setAssistantFull(false);
    setActiveTool(null);
    setMinimizedTool(null);
    setToolsOpen(false);
    setPrintOpen(false);
    setSigningSession(null);
    setSigningSheetOpen(false);
    setFocusCommentId(null);
    setAddCommentQuote(null);
    setPendingHomeTool(null);
    setHomeView("dashboard");
    setKartablTask(null);
    setApprovalOpen(false);
    setDadnoSeed(null);
    setPendingDashboardSign(false);
    setBlankSimpleMode(false);
    setSimpleSaveOpen(false);
    setSimpleSaveError(null);
  }, []);

  const openHomeView = useCallback((view: HomeView, opts?: { preservePendingSign?: boolean }) => {
    setFile(null);
    setActiveCaseId(null);
    setError(null);
    setMenuOpen(false);
    setSide("none");
    setAssistantOpen(false);
    setAssistantFull(false);
    setActiveTool(null);
    setMinimizedTool(null);
    setToolsOpen(false);
    setPrintOpen(false);
    setSigningSession(null);
    setSigningSheetOpen(false);
    setFocusCommentId(null);
    setAddCommentQuote(null);
    setPendingHomeTool(null);
    setKartablTask(null);
    setApprovalOpen(false);
    setDadnoSeed(null);
    if (!opts?.preservePendingSign) setPendingDashboardSign(false);
    setBlankSimpleMode(false);
    setSimpleSaveOpen(false);
    setSimpleSaveError(null);
    setHomeView(view);
  }, []);

  const openKartablTask = useCallback(
    (payload: KartablOpenPayload) => {
      setKartablTask(payload.task);
      void openNevisignDocument(payload.document);
    },
    [openNevisignDocument],
  );

  const openContractById = useCallback(
    (contractId: string, title: string) => {
      setHomeView("dashboard");
      void openNevisignDocument({
        id: contractId,
        title,
        kind: "contract",
        kind_label: "قرارداد",
        status: "",
        status_label: "",
        subject: "",
        jurisdiction: "",
        parties_summary: "",
        created_at: "",
        updated_at: "",
        progress_message: "",
        signed: false,
      });
    },
    [openNevisignDocument],
  );

  const openLocalOpenedFile = useCallback(
    (opened: OpenedFile) => {
      setHomeView("dashboard");
      setKartablTask(null);
      void openBuffer(opened.name, opened.buffer, opened.mimeType, opened.fileUri);
    },
    [openBuffer],
  );

  const openLocalDraft = useCallback(
    (draft: LocalDraft) => {
      openBlankSimpleDocument(
        {
          title: draft.title,
          html: draft.html || draft.text.replace(/\n/g, "<br/>"),
          text: draft.text,
        },
        { returnView: "contract-ai" },
      );
    },
    [openBlankSimpleDocument],
  );

  const openCase = useCallback((caseId: string) => {
    setFile(null);
    setActiveCaseId(caseId);
    setError(null);
    setMenuOpen(false);
    setSide("none");
    setAssistantOpen(false);
    setActiveTool(null);
    setToolsOpen(false);
    setHomeView("dashboard");
  }, []);

  const openComment = useCallback(
    (idOrIds: string) => {
      if (!settings.showComments) return;
      const ids = idOrIds.trim().split(/\s+/).filter(Boolean);
      const visible = readerComments.filter((c) => !c.deleted);
      const match =
        ids.map((id) => visible.find((c) => c.word_id === id)).find(Boolean) ??
        null;
      if (!match) {
        // Stale mark after delete — drop highlight chrome, don't reopen
        unwrapCommentMarks(ids);
        return;
      }
      suppressBriefly();
      window.getSelection()?.removeAllRanges();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      setFocusCommentId(match.word_id);
      setActiveCommentId(match.word_id);
      setSide("none");
      setAddCommentQuote(null);
    },
    [readerComments, settings.showComments, suppressBriefly, unwrapCommentMarks]
  );

  // PDF: tap comment marks in the transparent text layer
  useEffect(() => {
    if (file?.kind !== "pdf") return;
    const root = pdfContainerRef.current;
    if (!root) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      const mark = target?.closest(
        ".nr-comment-range, .nr-comment-anchor, .pdf-comment-pin"
      ) as HTMLElement | null;
      if (!mark) return;
      const ids =
        mark.getAttribute("data-comment-ids") ||
        mark.getAttribute("data-comment-id");
      if (!ids?.trim()) return;
      e.preventDefault();
      e.stopPropagation();
      openComment(ids.trim());
    };
    root.addEventListener("pointerdown", onPointerDown, true);
    return () => root.removeEventListener("pointerdown", onPointerDown, true);
  }, [file?.kind, openComment]);

  const [liveDocText, setLiveDocText] = useState("");

  // Keep assistant document_text fresh from live DOCX DOM / PDF extract
  useEffect(() => {
    if (!file) {
      setLiveDocText("");
      return;
    }
    const refresh = () => {
      if (file.kind === "docx") {
        const live = extractLiveDocxText(docxContainerRef.current);
        const fallback = annotations?.finalText ?? "";
        setLiveDocText((live || fallback).slice(0, OFFICE_DOCUMENT_TEXT_MAX));
      } else {
        setLiveDocText(pdfExtractedText.slice(0, OFFICE_DOCUMENT_TEXT_MAX));
      }
    };
    refresh();
    const id = window.setInterval(refresh, assistantOpen ? 2500 : 8000);
    return () => window.clearInterval(id);
  }, [annotations?.finalText, assistantOpen, file, pdfExtractedText, docxHtml]);

  const documentTextForAssistant = liveDocText;

  const shareDocument = useCallback(async () => {
    if (!file || sharingDoc) return;
    if (!file.buffer || file.buffer.byteLength === 0) {
      setError("این سند فقط ابری است و فایل برای اشتراک ندارد.");
      return;
    }
    setSharingDoc(true);
    setError(null);
    try {
      await shareOpenDocument({
        buffer: file.buffer,
        fileName: file.name,
        mimeType: file.mimeType,
      });
      appendLog("info", "share-file", `shared open document ${file.name}`, {
        bytes: file.buffer.byteLength,
        kind: file.kind,
      });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "اشتراک فایل ناموفق بود.";
      appendLog("error", "share-file", msg);
      setError(msg);
    } finally {
      setSharingDoc(false);
      setMenuOpen(false);
    }
  }, [file, sharingDoc]);

  const buildCommentsAndTcContext = useCallback(() => {
    const comments = readerComments
      .filter((c) => !c.deleted)
      .map(
        (c) =>
          `— ${c.author || "ناشناس"}: ${c.body}` +
          (c.quote ? ` (نقل: ${c.quote.slice(0, 120)})` : "")
      )
      .join("\n");
    const tc = (annotations?.revisions ?? [])
      .map(
        (r) =>
          `${r.type === "ins" ? "درج" : "حذف"} توسط ${r.author}: ${r.text.slice(0, 200)}`
      )
      .join("\n");
    return { comments, track_changes: tc };
  }, [annotations?.revisions, readerComments]);

  const runSummaryTool = useCallback(async () => {
    setActiveTool("summary");
    setMinimizedTool(null);
    setToolsOpen(false);
    const regenerating = Boolean(summaryText);
    setSummaryRegenerating(regenerating);
    setSummaryBusy(true);
    setSummaryError(null);
    try {
      let text = documentTextForAssistant;
      if (file?.kind === "pdf" && file.buffer.byteLength > 0) {
        const { extractPdfPlainText } = await import("./lib/pdfTextExtract");
        text = await extractPdfPlainText(file.buffer);
        setPdfExtractedText(text);
      }
      const ctx = buildCommentsAndTcContext();
      const result = await summarizeDocument(settings.nevisignApiBase, {
        document_text: text.slice(0, 48000),
        comments: ctx.comments,
        track_changes: ctx.track_changes,
        title: file?.name,
      });
      setSummaryText(result.summary);
      setSummaryHistory((h) => {
        const prev = regenerating && summaryText ? [summaryText, ...h] : h;
        return [result.summary, ...prev.filter((x) => x !== result.summary)].slice(
          0,
          12
        );
      });
    } catch (err) {
      if (handleApiAuthError(err, () => void runSummaryTool())) return;
      setSummaryError(err instanceof Error ? err.message : "خطا در خلاصه");
    } finally {
      setSummaryBusy(false);
      setSummaryRegenerating(false);
    }
  }, [
    buildCommentsAndTcContext,
    documentTextForAssistant,
    file,
    settings.nevisignApiBase,
    summaryText,
  ]);

  const runRiskTool = useCallback(async () => {
    setActiveTool("legal-risk");
    setMinimizedTool(null);
    setToolsOpen(false);
    setRiskBusy(true);
    setRiskError(null);
    try {
      let text = documentTextForAssistant;
      if (file?.kind === "pdf" && file.buffer.byteLength > 0) {
        const { extractPdfPlainText } = await import("./lib/pdfTextExtract");
        text = await extractPdfPlainText(file.buffer);
        setPdfExtractedText(text);
      }
      const ctx = buildCommentsAndTcContext();
      const result = await analyzeLegalRisk(settings.nevisignApiBase, {
        document_text: text.slice(0, 48000),
        comments: ctx.comments,
        track_changes: ctx.track_changes,
        title: file?.name,
      });
      setRiskResult(result);
    } catch (err) {
      if (handleApiAuthError(err, () => void runRiskTool())) return;
      setRiskError(err instanceof Error ? err.message : "خطا در تحلیل ریسک");
    } finally {
      setRiskBusy(false);
    }
  }, [
    buildCommentsAndTcContext,
    documentTextForAssistant,
    file,
    settings.nevisignApiBase,
  ]);

  const launchToolWithFile = useCallback(
    (id: ToolId) => {
      setPendingHomeTool(null);
      setMinimizedTool(null);
      if (id === "summary") {
        requireAuth(() => void runSummaryTool());
        return;
      }
      if (id === "legal-risk") {
        requireAuth(() => void runRiskTool());
        return;
      }
      if (id === "compare") {
        setActiveTool("compare");
        return;
      }
      if (id === "word-to-pdf") {
        if (file?.kind !== "docx") {
          setError("تبدیل Word به PDF فقط برای اسناد Word است.");
          return;
        }
        setPrintOpen(true);
        return;
      }
      if (id === "pdf-split") {
        setActiveTool("pdf-split");
        return;
      }
      if (id === "pdf-merge") {
        setActiveTool("pdf-merge");
        return;
      }
      if (id === "to-image") {
        if (file?.kind !== "pdf") {
          setError("تبدیل PDF به تصویر فقط برای فایل‌های PDF است.");
          return;
        }
        setActiveTool("to-image");
        return;
      }
      if (id === "assistant") {
        requireAuth(() => {
          setAssistantScopedSelection(null);
          setPendingQuote(null);
          setFileMissingHint(null);
          setAssistantOpen(true);
          setAssistantFull(false);
        });
      }
    },
    [file, requireAuth, runRiskTool, runSummaryTool]
  );

  const onSelectTool = useCallback(
    (id: ToolId) => {
      setToolsOpen(false);
      if (!file) {
        // Home: pick file(s) first, then run the tool
        setPendingHomeTool(id);
        if (id === "pdf-merge") {
          // merge can start with empty list — open sheet directly
          setActiveTool("pdf-merge");
          setPendingHomeTool(null);
          return;
        }
        if (id === "assistant") {
          setPendingHomeTool(null);
          requireAuth(() => {
            setAssistantScopedSelection(null);
            setPendingQuote(null);
            setFileMissingHint(null);
            setAssistantOpen(true);
            setAssistantFull(false);
          });
          return;
        }
        if (id === "compare") {
          setMinimizedTool(null);
          setActiveTool("compare");
          setPendingHomeTool(null);
          return;
        }
        if (id === "pdf-split") {
          setMinimizedTool(null);
          setActiveTool("pdf-split");
          setPendingHomeTool(null);
          return;
        }
        if (id === "scan-document") {
          setPendingHomeTool(null);
          setScanInitialDocId(null);
          setScanStartLibrary(false);
          setHomeView("contract-scan");
          return;
        }
        if (
          id === "word-to-pdf" ||
          id === "to-image" ||
          id === "summary" ||
          id === "legal-risk"
        ) {
          fileInputRef.current?.click();
          return;
        }
        fileInputRef.current?.click();
        return;
      }
      launchToolWithFile(id);
    },
    [file, launchToolWithFile, requireAuth]
  );

  // After home file pick, continue pending tool
  useEffect(() => {
    if (!file || !pendingHomeTool) return;
    const id = pendingHomeTool;
    setPendingHomeTool(null);
    launchToolWithFile(id);
  }, [file, pendingHomeTool, launchToolWithFile]);

  useEffect(() => {
    if (!file || !pendingDashboardSign) return;
    setPendingDashboardSign(false);
    setSignatureActionError(null);
    setSignatureActionResult(null);
    setSignatureActionOpen(true);
  }, [file, pendingDashboardSign]);

  const minimizeActiveTool = useCallback(() => {
    if (!activeTool) return;
    setMinimizedTool(activeTool);
    setActiveTool(null);
  }, [activeTool]);

  const restoreMinimizedTool = useCallback(() => {
    if (!minimizedTool) return;
    setActiveTool(minimizedTool);
    setMinimizedTool(null);
  }, [minimizedTool]);

  const toolLabel = useCallback((id: ToolId) => {
    return READER_TOOLS.find((t) => t.id === id)?.label ?? "ابزار";
  }, []);

  const ensureOpenFileContractId = useCallback(async (): Promise<string> => {
    if (!file) throw new Error("ابتدا یک فایل باز کنید.");
    if (file.nevisignDocumentId) return file.nevisignDocumentId;
    if (!file.buffer || file.buffer.byteLength === 0) {
      throw new Error("این سند فایل محلی برای ورود به نویساین ندارد.");
    }
    const uploadFile = new File([file.buffer], file.name, {
      type:
        file.mimeType ||
        (file.kind === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document"),
    });
    const contract = await createContractFromUpload(settings.nevisignApiBase, uploadFile);
    setFile((cur) =>
      cur && cur.name === file.name
        ? { ...cur, nevisignDocumentId: contract.id }
        : cur,
    );
    appendLog("info", "signature", "imported open file for signature", {
      contractId: contract.id,
      fileName: file.name,
      kind: file.kind,
    });
    return contract.id;
  }, [file, settings.nevisignApiBase]);

  const startSelfSignature = useCallback(async () => {
    if (signatureActionBusy) return;
    requireAuth(async () => {
      setSignatureActionBusy(true);
      setSignatureActionError(null);
      setSignatureActionResult(null);
      try {
        if (!user?.id) throw new Error("اطلاعات کاربر در دسترس نیست.");
        if (!user.phone?.trim()) {
          throw new Error("برای امضای سند، شماره موبایل حساب کاربری لازم است.");
        }
        const contractId = await ensureOpenFileContractId();
        let verified = false;
        try {
          const profile = await fetchEsignProfile(settings.nevisignApiBase);
          verified = Boolean(profile.identityVerified || profile.hasRootCertificate);
        } catch {
          verified = false;
        }
        const mode = verified ? "in_platform" : "gateway";
        const workflow = await startContractSignature(settings.nevisignApiBase, contractId, {
          mode,
          skipSms: true,
          signers: [
            {
              userId: user.id,
              phone: user.phone,
              displayName: user.fullName || "من",
              order: 1,
              signatureType: mode === "gateway" ? "gateway" : "in_platform",
              identityMethod: verified ? "ministry_cert" : "account",
            },
          ],
        });
        const process = workflow.activeSignature ?? null;
        setSignatureActionResult({
          contractId,
          mode,
          processId: process?.id,
          signers: process?.signers ?? [],
        });
        const pending = await detectPendingSignature(settings.nevisignApiBase, contractId);
        setSigningSession(pending);
        setSigningSheetOpen(Boolean(pending?.canAct || pending?.gatewayToken));
        appendLog("info", "signature", "started self signature", {
          contractId,
          mode,
        });
      } catch (err) {
        if (handleApiAuthError(err, () => void startSelfSignature())) return;
        const msg = err instanceof Error ? err.message : "شروع امضا ناموفق بود";
        setSignatureActionError(msg);
        appendLog("error", "signature", msg);
      } finally {
        setSignatureActionBusy(false);
      }
    });
  }, [
    ensureOpenFileContractId,
    requireAuth,
    settings.nevisignApiBase,
    signatureActionBusy,
    user?.fullName,
    user?.id,
    user?.phone,
  ]);

  const startOthersSignature = useCallback(
    async (input: {
      includeSelf: boolean;
      notifySms: boolean;
      redirectUrl?: string;
      failureRedirectUrl?: string;
      signers: SignatureActionSigner[];
    }) => {
      if (signatureActionBusy) return;
      requireAuth(async () => {
        setSignatureActionBusy(true);
        setSignatureActionError(null);
        setSignatureActionResult(null);
        try {
          const contractId = await ensureOpenFileContractId();
          const signers = [
            ...(input.includeSelf && user?.id
              ? [
                  {
                    userId: user.id,
                    phone: user.phone ?? undefined,
                    displayName: user.fullName || "من",
                    order: 1,
                    signatureType: "gateway",
                    identityMethod: "account",
                  },
                ]
              : []),
            ...input.signers.map((s) => ({
              phone: s.phone,
              displayName: s.fullName,
              partyName: s.nationalId ? `کد ملی: ${s.nationalId}` : undefined,
              order: 1,
              signatureType: "gateway",
              identityMethod: "account",
            })),
          ];
          if (!signers.length) throw new Error("حداقل یک امضاکننده لازم است.");
          const workflow = await startContractSignature(settings.nevisignApiBase, contractId, {
            mode: "gateway",
            skipSms: !input.notifySms,
            redirectUrl: input.redirectUrl ?? null,
            failureRedirectUrl: input.failureRedirectUrl ?? null,
            signers,
          });
          const process = workflow.activeSignature ?? null;
          setSignatureActionResult({
            contractId,
            mode: "gateway",
            processId: process?.id,
            signers: process?.signers ?? [],
          });
          const pending = await detectPendingSignature(settings.nevisignApiBase, contractId);
          setSigningSession(pending);
          setSigningSheetOpen(Boolean(pending?.canAct || pending?.gatewayToken));
          appendLog("info", "signature", "started gateway signature", {
            contractId,
            signers: signers.length,
            notifySms: input.notifySms,
          });
        } catch (err) {
          if (handleApiAuthError(err, () => void startOthersSignature(input))) return;
          const msg = err instanceof Error ? err.message : "ارسال برای امضا ناموفق بود";
          setSignatureActionError(msg);
          appendLog("error", "signature", msg);
        } finally {
          setSignatureActionBusy(false);
        }
      });
    },
    [
      ensureOpenFileContractId,
      requireAuth,
      settings.nevisignApiBase,
      signatureActionBusy,
      user?.fullName,
      user?.id,
      user?.phone,
    ],
  );

  const cloudSave = useCallback(async () => {
    if (!file || cloudSaving) return;
    requireAuth(async () => {
      setCloudSaving(true);
      setError(null);
      try {
        let text = documentTextForAssistant;
        if (!text.trim() && file.kind === "docx") {
          text = annotations?.finalText ?? "";
        }
        if (text.trim().length < 10) {
          throw new Error(
            "متن کافی برای ذخیره در نویساین استخراج نشد. ابتدا سند را باز کنید."
          );
        }
        const result = await createNevisignDocument(settings.nevisignApiBase, {
          mode: "import",
          text: text.slice(0, 50000),
          title: file.name,
          allocate_number: false,
          attachments: [
            {
              name: file.name,
              content: text.slice(0, 80000),
            },
          ],
        });
        appendLog("info", "cloud", "saved document", result);
        setError(null);
        alert(`سند در نویساین ذخیره شد (شناسه: ${result.id}).`);
      } catch (err) {
        if (handleApiAuthError(err, () => void cloudSave())) return;
        const msg = err instanceof Error ? err.message : "ذخیره ناموفق بود";
        appendLog("error", "cloud", msg);
        setError(msg);
      } finally {
        setCloudSaving(false);
      }
    });
  }, [
    annotations?.finalText,
    cloudSaving,
    documentTextForAssistant,
    file,
    requireAuth,
    settings.nevisignApiBase,
  ]);

  const leaveBlankSimpleDocument = useCallback(() => {
    const returnView = blankSimpleReturnView;
    setFile(null);
    setBlankSimpleMode(false);
    setSimpleSaveOpen(false);
    setSimpleSaveError(null);
    setError(null);
    setMenuOpen(false);
    setSide("none");
    setAssistantOpen(false);
    setAssistantFull(false);
    setActiveTool(null);
    setMinimizedTool(null);
    setToolsOpen(false);
    setHomeView(returnView);
  }, [blankSimpleReturnView]);

  const readBlankSimpleContent = useCallback(() => {
    const html = extractLiveDocxHtml(docxContainerRef.current);
    const text =
      extractLiveDocxText(docxContainerRef.current) ||
      annotations?.finalText ||
      "";
    return { html, text };
  }, [annotations?.finalText]);

  const confirmSimpleDocSave = useCallback(
    async (opts: { title: string; destination: SimpleDocSaveDestination }) => {
      if (!file || !blankSimpleMode || simpleSaveBusy) return;
      const title = opts.title.trim() || "سند بدون عنوان";
      const name = simpleDocFileName(title);
      const { html, text } = readBlankSimpleContent();

      if (opts.destination === "phone") {
        setSimpleSaveBusy(true);
        setSimpleSaveError(null);
        try {
          const bytes = await buildSimpleDocxBytes({ html, text });
          await saveAndShareBytes(bytes, name, DOCX_MIME);
          setFile((prev) =>
            prev
              ? {
                  ...prev,
                  name,
                  mimeType: DOCX_MIME,
                  buffer: bytes.buffer.slice(
                    bytes.byteOffset,
                    bytes.byteOffset + bytes.byteLength,
                  ) as ArrayBuffer,
                }
              : prev,
          );
          setSimpleSaveOpen(false);
          appendLog("info", "file", `saved blank simple docx to device ${name}`);
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : "ذخیره روی گوشی ناموفق بود";
          setSimpleSaveError(msg);
          appendLog("error", "file", msg);
        } finally {
          setSimpleSaveBusy(false);
        }
        return;
      }

      requireAuth(async () => {
        setSimpleSaveBusy(true);
        setCloudSaving(true);
        setSimpleSaveError(null);
        setError(null);
        try {
          const body = text.trim() || title;
          const result = await createNevisignDocument(settings.nevisignApiBase, {
            mode: "import",
            text: body.slice(0, 50000),
            title,
            allocate_number: false,
            attachments: [
              {
                name,
                content: body.slice(0, 80000),
              },
            ],
          });
          setFile((prev) => (prev ? { ...prev, name } : prev));
          setSimpleSaveOpen(false);
          appendLog("info", "cloud", "saved blank simple document", result);
          alert(`سند در نویساین ذخیره شد (شناسه: ${result.id}).`);
        } catch (err) {
          if (
            handleApiAuthError(err, () =>
              void confirmSimpleDocSave({
                title,
                destination: "nevisign",
              }),
            )
          ) {
            return;
          }
          const msg = err instanceof Error ? err.message : "ذخیره ناموفق بود";
          setSimpleSaveError(msg);
          appendLog("error", "cloud", msg);
        } finally {
          setSimpleSaveBusy(false);
          setCloudSaving(false);
        }
      });
    },
    [
      blankSimpleMode,
      file,
      readBlankSimpleContent,
      requireAuth,
      settings.nevisignApiBase,
      simpleSaveBusy,
    ],
  );

  const runPrint = useCallback(async () => {
    if (!file) return;
    setPrintBusy(true);
    try {
      await printOpenDocument({
        settings: settings.print,
        jobName: file.name,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "چاپ ناموفق";
      appendLog("error", "print", msg);
      setError(msg);
    } finally {
      setPrintBusy(false);
      setPrintOpen(false);
    }
  }, [file, settings.print]);

  const reopenChatFile = useCallback(
    async (thread: FileChatThread): Promise<boolean> => {
      try {
        const resolved = await resolveDocumentForThread(thread);
        if (!resolved) {
          setFileMissingHint("فایل در دسترس نیست");
          setError(
            "فایل مرتبط پیدا نشد. سند را دوباره از حافظهٔ گوشی باز کنید تا به گفتگو وصل شود."
          );
          return false;
        }
        await openBuffer(
          resolved.fileName,
          resolved.buffer,
          resolved.mimeType || thread.mimeType,
          resolved.fileUri || thread.fileUri
        );
        updateChatFileMeta(thread.id, {
          fileUri: resolved.fileUri || thread.fileUri,
          filePath: resolved.filePath,
          mimeType: resolved.mimeType || thread.mimeType,
          byteLength: resolved.buffer.byteLength,
        });
        setSide("none");
        setFileMissingHint(null);
        setChatList(listChatThreads());
        return true;
      } catch (err) {
        setFileMissingHint("فایل در دسترس نیست");
        setError(err instanceof Error ? err.message : "فایل در دسترس نیست");
        appendLog("warn", "chat", "reopen file missing", {
          fileName: thread.fileName,
          fileUri: thread.fileUri,
          filePath: thread.filePath,
        });
        return false;
      }
    },
    [openBuffer]
  );

  const openChatFullscreen = useCallback(
    async (thread: FileChatThread) => {
      requireAuth(async () => {
        setActiveChatId(thread.id);
        setAssistantOpen(true);
        setAssistantFull(true);
        setSide("none");
        setMenuOpen(false);
        setAssistantScopedSelection(null);
        setPendingQuote(null);

        const ok = await reopenChatFile(thread);
        if (!ok) setFileMissingHint("فایل در دسترس نیست");
        else setFileMissingHint(null);
      });
    },
    [requireAuth, reopenChatFile]
  );

  const installUpdate = useCallback(async () => {
    if (!updateManifest) return;
    if (!Capacitor.isNativePlatform()) {
      setUpdateError("نصب مستقیم فقط روی اندروید پشتیبانی می‌شود.");
      return;
    }
    setUpdateBusy(true);
    setUpdateError(null);
    setUpdateProgress(0);
    try {
      await downloadAndInstallApk(updateManifest.apkUrl, setUpdateProgress);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "نصب به‌روزرسانی ناموفق بود";
      setUpdateError(msg);
      appendLog("error", "update", msg);
    } finally {
      setUpdateBusy(false);
    }
  }, [updateManifest]);

  const checkUpdateFromMenu = useCallback(async () => {
    setMenuOpen(false);
    if (!Capacitor.isNativePlatform()) {
      setUpdateToast("به‌روزرسانی خودکار فقط در اپ اندروید است");
      window.setTimeout(() => setUpdateToast(null), 2800);
      return;
    }
    if (updateCheckBusy || updateBusy) return;
    setUpdateCheckBusy(true);
    setUpdateToast(null);
    try {
      const result = await checkForUpdate();
      if (result.updateAvailable) {
        setUpdateManifest(result.manifest);
        setForceUpdate(result.forceUpdate);
        setUpdateError(null);
        // Same path as banner: show banner so user can tap دانلود آپدیت
        // (native ApkInstaller.downloadAndInstall for v6+).
      } else {
        setUpdateToast("نسخه فعلی به‌روز است");
        window.setTimeout(() => setUpdateToast(null), 2800);
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "بررسی به‌روزرسانی ناموفق بود";
      setUpdateError(msg);
      setUpdateToast(msg);
      window.setTimeout(() => setUpdateToast(null), 3200);
      appendLog("warn", "update", "manual check failed", err);
    } finally {
      setUpdateCheckBusy(false);
    }
  }, [updateBusy, updateCheckBusy]);

  const showEsignBanner =
    isAuthenticated &&
    esignBannerVisible &&
    homeView === "dashboard" &&
    !file;
  const showHeaderChrome =
    homeView !== "esign-onboard" &&
    (Boolean(file) ||
      Boolean(activeCaseId) ||
      homeView !== "dashboard" ||
      dashboardScrolled);

  return (
    <div
      className={`app${file ? " has-file" : " empty"}${showHeaderChrome ? "" : " no-chrome"}`}
    >
      <input
        ref={fileInputRef}
        className="sr-only"
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={(e) => {
          void onPickFile(e.target.files);
          e.target.value = "";
        }}
      />

      {updateManifest && (
        <UpdateBanner
          manifest={updateManifest}
          force={forceUpdate}
          progress={updateProgress}
          busy={updateBusy}
          error={updateError}
          onUpdate={() => void installUpdate()}
          onDismiss={
            forceUpdate
              ? undefined
              : () => {
                  setUpdateManifest(null);
                }
          }
        />
      )}

      {updateToast && !updateManifest && (
        <div className="update-toast" role="status">
          {updateToast}
        </div>
      )}

      {showHeaderChrome ? (
        <header className="chrome chrome-sticky">
          <div className="chrome-start" ref={menuRef}>
            {blankSimpleMode && file ? (
              <button
                type="button"
                className="icon-btn menu-btn"
                aria-label="بازگشت"
                title="بازگشت"
                onClick={leaveBlankSimpleDocument}
              >
                <ArrowRight size={18} />
              </button>
            ) : (
              <button
                type="button"
                className="icon-btn menu-btn"
                aria-label="منو"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                {menuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            )}
          </div>
          {blankSimpleMode && file ? null : (
            <button
              type="button"
              className="brand"
              title="داشبورد نویساین"
              aria-label="داشبورد"
              onClick={goHome}
            >
              <img src="/brand/nevisign-logo.svg" alt="" className="brand-mark" />
              <span className="brand-text">نویساین</span>
            </button>
          )}
          <div className="doc-title">{topTitle}</div>
          <div className="chrome-end">
            {blankSimpleMode && file ? (
              <button
                type="button"
                className="icon-btn primary chrome-save-btn"
                aria-label="ذخیره"
                title="ذخیره"
                disabled={simpleSaveBusy || cloudSaving}
                onClick={() => {
                  setSimpleSaveError(null);
                  setSimpleSaveOpen(true);
                }}
              >
                <Save size={15} />
                ذخیره
              </button>
            ) : null}
            <AppAuthChip onOpenProfile={() => setProfileOpen(true)} />
          </div>
        </header>
      ) : null}

      {menuOpen && (
        <div
          className="app-drawer-backdrop"
          role="presentation"
          onClick={() => setMenuOpen(false)}
        >
          <nav
            className="app-drawer"
            role="dialog"
            aria-label="منو"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-drawer-header">
              <span className="app-drawer-title">منو</span>
              <button
                type="button"
                className="icon-btn"
                aria-label="بستن"
                onClick={() => setMenuOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="app-drawer-body">
              <button type="button" className="app-drawer-item" onClick={goHome}>
                <LayoutDashboard size={18} />
                داشبورد
              </button>
              <button
                type="button"
                className="app-drawer-item"
                onClick={() => requireAuth(() => openHomeView("esign-onboard"))}
              >
                <ShieldCheck size={18} />
                احراز هویت و گواهی امضا
              </button>
              <button
                type="button"
                className="app-drawer-item"
                onClick={() => openHomeView("chat-list")}
              >
                <MessagesSquare size={18} />
                گفتگوها
              </button>
              <button
                type="button"
                className="app-drawer-item"
                onClick={() => openHomeView("nevisign-docs")}
              >
                <Cloud size={18} />
                لیست اسناد در نویساین
              </button>
              <button
                type="button"
                className="app-drawer-item"
                onClick={() => openHomeView("cases-list")}
              >
                <FolderOpen size={18} />
                لیست پرونده‌ها در نویساین
              </button>
              <button
                type="button"
                className="app-drawer-item"
                disabled={updateCheckBusy || updateBusy}
                onClick={() => void checkUpdateFromMenu()}
              >
                <RefreshCw size={18} />
                {updateCheckBusy ? "در حال بررسی…" : "بررسی به‌روزرسانی"}
              </button>
              <button
                type="button"
                className="app-drawer-item"
                onClick={() => toggleSide("logs")}
              >
                <ScrollText size={18} />
                لاگ‌ها
              </button>
              <button
                type="button"
                className="app-drawer-item"
                onClick={() => {
                  if (file) {
                    toggleSide("settings");
                    return;
                  }
                  openHomeView("settings");
                }}
              >
                <Settings2 size={18} />
                تنظیمات
              </button>
            </div>
            <div className="app-drawer-footer">
              <p className="app-drawer-version">
                {formatDrawerVersion(drawerVersionName)}
              </p>
              <p className="app-drawer-ownership">
                تمامی حقوق نویساین متعلق به محامی (شرکت مدافعان فناوری حامی)
                است.
              </p>
              {NEVISIGN_SITE_URL ? (
                <a
                  className="app-drawer-site"
                  href={NEVISIGN_SITE_URL}
                  target="_blank"
                  rel="noreferrer"
                  dir="ltr"
                >
                  {siteHostLabel(NEVISIGN_SITE_URL)}
                </a>
              ) : null}
            </div>
          </nav>
        </div>
      )}

      {file && (
        <ReaderToolbar
          kind={file.kind}
          zoom={zoom}
          docxViewMode={settings.docxViewMode}
          showComments={side === "comments"}
          cloudSaving={cloudSaving}
          sharing={sharingDoc}
          signing={signatureActionBusy}
          canShare={Boolean(file.buffer && file.buffer.byteLength > 0)}
          onZoomIn={() =>
            setZoom((z) => {
              const next = clampZoom(z + 0.15);
              zoomAtViewportCenter(getCanvasScroll(), z, next);
              return next;
            })
          }
          onZoomOut={() =>
            setZoom((z) => {
              const next = clampZoom(z - 0.15);
              zoomAtViewportCenter(getCanvasScroll(), z, next);
              return next;
            })
          }
          onResetZoom={() =>
            setZoom((z) => {
              zoomAtViewportCenter(getCanvasScroll(), z, 1);
              return 1;
            })
          }
          onDocxViewMode={(mode) => {
            updateSettings({ docxViewMode: mode });
            setZoom(1);
            setDocxPage(1);
            requestAnimationFrame(() => {
              const sc = document.querySelector(
                ".canvas-scroll"
              ) as HTMLElement | null;
              sc?.scrollTo({ left: 0, top: sc.scrollTop });
            });
          }}
          onToggleComments={
            file.kind === "docx" || file.kind === "pdf"
              ? () => {
                  if (side === "comments") {
                    setSide("none");
                  } else {
                    updateSettings({ showComments: true });
                    setSide("comments");
                    setMenuOpen(false);
                    if (file.kind === "pdf") setPdfTextLayer(true);
                  }
                }
              : undefined
          }
          onShare={() => void shareDocument()}
          onCloudSave={() => void cloudSave()}
          onSign={() => {
            requireAuth(() => {
              setSignatureActionError(null);
              setSignatureActionResult(null);
              setSignatureActionOpen(true);
            });
          }}
        />
      )}

      <div
        className={`workspace${side !== "none" ? " with-side" : ""}${signingSession ? " with-signing" : ""}`}
      >
        <div className="reader-pane no-toolbar">
          <div
            className={`canvas-scroll${selectionState.visible ? " has-selection-bar" : ""} has-fab-pad`}
          >
            {error && (
              <div className="status-banner" role="alert">
                {error}
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "dashboard" && (
              <div className="dashboard-empty-state">
                <HomeDashboard
                  apiBase={settings.nevisignApiBase}
                  onSelectTool={onSelectTool}
                  onOpenKartablTask={openKartablTask}
                  openFilePicker={openFilePicker}
                  openingDocId={openingNevisignId}
                  onOpenContractHub={() => openHomeView("contract-hub")}
                  onOpenDadnoAction={(mode) => {
                    if (mode === "summary") {
                      onSelectTool("summary");
                      return;
                    }
                    if (mode === "risk") {
                      onSelectTool("legal-risk");
                      return;
                    }
                    /* مشاوره هوشمند → ContractChat-style page (no auto-send) */
                    setDadnoSeed({
                      message: "",
                      mode: "consult",
                      returnTo: "dashboard",
                    });
                    openHomeView("dadno-chat");
                  }}
                  onOpenDocuments={() => openHomeView("nevisign-docs")}
                  onOpenCases={() => openHomeView("cases-list")}
                  onOpenSignStart={() => openHomeView("sign-start")}
                  onOpenLocalDraft={openLocalDraft}
                  onOpenScannedDoc={(docId) => {
                    setScanInitialDocId(docId);
                    setScanStartLibrary(false);
                    openHomeView("contract-scan");
                  }}
                  onOpenScanLibrary={() => {
                    setScanInitialDocId(null);
                    setScanStartLibrary(true);
                    openHomeView("contract-scan");
                  }}
                  menuOpen={menuOpen}
                  onToggleMenu={() => setMenuOpen((o) => !o)}
                  onOpenProfile={() => setProfileOpen(true)}
                  showEsignBanner={showEsignBanner}
                  onStartEsign={() => {
                    requireAuth(() => setHomeView("esign-onboard"));
                  }}
                  onDismissEsign={() => {
                    setEsignBannerVisible(false);
                    if (user?.id) {
                      const prev = loadEsignLocal(user.id);
                      saveEsignLocal({
                        userId: user.id,
                        identityVerified: prev?.identityVerified ?? false,
                        hasRootCertificate: prev?.hasRootCertificate ?? false,
                        onboardingComplete: prev?.onboardingComplete ?? false,
                        fingerprintEnabled: prev?.fingerprintEnabled ?? false,
                        patternEnabled: prev?.patternEnabled ?? false,
                        unlockPasswordHash: prev?.unlockPasswordHash ?? null,
                        patternHash: prev?.patternHash ?? null,
                        signatureSvg: prev?.signatureSvg ?? null,
                        province: prev?.province ?? null,
                        city: prev?.city ?? null,
                        address: prev?.address ?? null,
                        bannerDismissedAt: new Date().toISOString(),
                      });
                    }
                  }}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "sign-start" && (
              <div className="dashboard-empty-state">
                <SignStartPage
                  isAuthenticated={isAuthenticated}
                  onBack={() => openHomeView("dashboard")}
                  onPickLocalFile={(files) => {
                    setPendingDashboardSign(true);
                    void onPickFile(files);
                  }}
                  onOpenCloudDocuments={() => {
                    if (!isAuthenticated) {
                      requireAuth(() => {
                        setPendingDashboardSign(true);
                        openHomeView("nevisign-docs", { preservePendingSign: true });
                      });
                      return;
                    }
                    setPendingDashboardSign(true);
                    openHomeView("nevisign-docs", { preservePendingSign: true });
                  }}
                  onOpenEsignSetup={() =>
                    requireAuth(() => openHomeView("esign-onboard"))
                  }
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "nevisign-docs" && (
              <div className="dashboard-empty-state list-page-wrap">
                <NevisignDocsPanel
                  apiBase={settings.nevisignApiBase}
                  openingId={openingNevisignId}
                  authenticated={isAuthenticated}
                  onBack={() => openHomeView("dashboard")}
                  onOpenDocument={(doc) => {
                    setKartablTask(null);
                    void openNevisignDocument(doc);
                  }}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "chat-list" && (
              <div className="dashboard-empty-state list-page-wrap">
                <ChatListPage
                  threads={chatList.length ? chatList : listChatThreads()}
                  activeChatId={activeChatId}
                  onOpenChat={(t) => {
                    void openChatFullscreen(t);
                  }}
                  onReopenFile={(t) => void reopenChatFile(t)}
                  onClose={() => openHomeView("dashboard")}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "cases-list" && (
              <div className="dashboard-empty-state list-page-wrap">
                <CasesListPage
                  apiBase={settings.nevisignApiBase}
                  onBack={() => openHomeView("dashboard")}
                  onOpenCase={openCase}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "settings" && (
              <div className="dashboard-empty-state list-page-wrap">
                <SettingsPanel
                  settings={settings}
                  onChange={updateSettings}
                  versionLabel={versionLabel}
                  onBack={() => openHomeView("dashboard")}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "esign-onboard" && (
              <div className="dashboard-empty-state case-page-wrap">
                <EsignOnboardFlow
                  apiBase={settings.nevisignApiBase}
                  onBack={() => setHomeView("dashboard")}
                  onComplete={() => {
                    setEsignBannerVisible(false);
                    setHomeView("dashboard");
                  }}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "contract-hub" && (
              <div className="dashboard-empty-state">
                <ContractDraftHub
                  onBack={() => setHomeView("dashboard")}
                  onSelect={(action) => {
                    if (action === "ai") setHomeView("contract-ai");
                    else if (action === "simple") {
                      openBlankSimpleDocument();
                    }                     else if (action === "upload") setHomeView("contract-upload");
                    else if (action === "scan") {
                      setScanInitialDocId(null);
                      setScanStartLibrary(false);
                      setHomeView("contract-scan");
                    } else if (action === "template") {
                      /* soon */
                    }
                  }}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "contract-ai" && (
              <div className="dashboard-empty-state">
                <ContractAiCreatePage
                  apiBase={settings.nevisignApiBase}
                  onBack={() => setHomeView("contract-hub")}
                  onOpenContract={openContractById}
                  onOpenLocalDraft={openLocalDraft}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "contract-upload" && (
              <div className="dashboard-empty-state">
                <DocumentUploadPage
                  apiBase={settings.nevisignApiBase}
                  onBack={() => setHomeView("contract-hub")}
                  onOpenContract={openContractById}
                  onOpenLocalFile={openLocalOpenedFile}
                  onStartScan={() => {
                    setScanInitialDocId(null);
                    setScanStartLibrary(false);
                    setHomeView("contract-scan");
                  }}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "contract-scan" && (
              <div className="dashboard-empty-state">
                <ScanDocumentPage
                  key={`${scanInitialDocId ?? "new"}-${scanStartLibrary ? "lib" : "edit"}`}
                  apiBase={settings.nevisignApiBase}
                  onBack={() => {
                    setScanInitialDocId(null);
                    setScanStartLibrary(false);
                    setHomeView("contract-hub");
                  }}
                  onOpenPdf={openLocalOpenedFile}
                  onOpenContract={openContractById}
                  initialDocId={scanInitialDocId}
                  startInLibrary={scanStartLibrary}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "dadno-hub" && (
              <div className="dashboard-empty-state">
                <DadnoHubPage
                  onBack={() => setHomeView("dashboard")}
                  onSelectAiTool={(id) => {
                    setHomeView("dashboard");
                    onSelectTool(id);
                  }}
                  onStartChat={(seed) => {
                    setDadnoSeed(seed);
                    setHomeView("dadno-chat");
                  }}
                />
              </div>
            )}
            {!file && !error && !activeCaseId && homeView === "dadno-chat" && dadnoSeed && (
              <div className="dashboard-empty-state dadno-chat-wrap">
                <DadnoChatPage
                  apiBase={settings.nevisignApiBase}
                  seedMessage={dadnoSeed.message}
                  attachmentName={dadnoSeed.attachmentName}
                  mode={dadnoSeed.mode ?? null}
                  onBack={() => {
                    const back = dadnoSeed.returnTo ?? "dadno-hub";
                    setDadnoSeed(null);
                    setHomeView(back);
                  }}
                />
              </div>
            )}
            {!file && activeCaseId && (
              <div className="dashboard-empty-state case-page-wrap">
                <CaseDetailPage
                  caseId={activeCaseId}
                  apiBase={settings.nevisignApiBase}
                  onBack={goHome}
                />
              </div>
            )}
            {!file && error && !activeCaseId && (
              <div className="empty-state">
                <div className="empty-actions">
                  <button
                    type="button"
                    className="icon-btn primary empty-cta"
                    onClick={openFilePicker}
                  >
                    <FileUp size={17} />
                    باز کردن فایل
                  </button>
                </div>
              </div>
            )}
            {file?.kind === "pdf" && (
              <div ref={pdfContainerRef} className="pdf-selection-root">
                <PdfViewer
                  ref={pdfRef}
                  buffer={file.buffer}
                  zoom={zoom}
                  textLayerEnabled={pdfTextLayer}
                  commentPins={readerComments
                    .filter((c) => !c.deleted && c.pdfAnchor)
                    .map((c) => ({
                      id: c.word_id,
                      page: c.pdfAnchor!.page,
                      xNorm: c.pdfAnchor!.xNorm,
                      yNorm: c.pdfAnchor!.yNorm,
                    }))}
                  onPageCount={setPdfPageCount}
                  onVisiblePage={setPdfPage}
                  onError={(msg) => setError(msg)}
                  onCommentPinClick={(id) => openComment(id)}
                  onLongPressComment={beginPdfLongPressComment}
                />
              </div>
            )}
            {file?.kind === "docx" && (
              <DocxViewer
                html={docxHtml}
                pageSize={docxPageSize}
                viewMode={settings.docxViewMode}
                zoom={zoom}
                showTrackChanges={settings.showTrackChanges}
                showComments={settings.showComments}
                activeCommentId={activeCommentId || focusCommentId}
                editable={!viewingComment && !addingComment}
                containerRef={docxContainerRef}
                onCommentClick={openComment}
                onRevisionTap={(info) => {
                  const kind = info.type === "ins" ? "درج" : "حذف";
                  setRevToast(
                    `${kind} توسط ${info.author}` +
                      (info.date ? ` · ${info.date}` : "")
                  );
                }}
                onPageCount={setDocxPageCount}
                onVisiblePage={setDocxPage}
              />
            )}
          </div>
          {file && (
            <>
              <PageScrollbar
                page={file.kind === "pdf" ? pdfPage : docxPage}
                pageCount={
                  file.kind === "pdf"
                    ? pdfPageCount
                    : settings.docxViewMode === "paginated"
                      ? docxPageCount
                      : docxPageCount || 1
                }
              />
              <PageHud
                page={file.kind === "pdf" ? pdfPage : docxPage}
                pageCount={
                  file.kind === "pdf"
                    ? pdfPageCount
                    : settings.docxViewMode === "paginated"
                      ? docxPageCount
                      : Math.max(1, docxPageCount)
                }
              />
            </>
          )}
        </div>

        {side !== "none" && (
          <aside className="side-panel" aria-label={sideTitle}>
            <div className="side-header">
              <span>{sideTitle}</span>
              <button
                type="button"
                className="icon-btn compact"
                onClick={() => setSide("none")}
              >
                بستن
              </button>
            </div>
            <div className="side-body">
              {side === "comments" && (
                <CommentsSidebar
                  comments={readerComments}
                  revisions={annotations?.revisions ?? []}
                  activeId={activeCommentId}
                  onSelect={(id) => {
                    openComment(id);
                  }}
                />
              )}
              {side === "settings" && (
                <SettingsPanel
                  settings={settings}
                  onChange={updateSettings}
                  versionLabel={versionLabel}
                />
              )}
              {side === "nevisign" && (
                <NevisignDocsPanel
                  apiBase={settings.nevisignApiBase}
                  openingId={openingNevisignId}
                  authenticated={isAuthenticated}
                  onOpenDocument={(doc) => {
                    void openNevisignDocument(doc);
                  }}
                />
              )}
              {side === "logs" && (
                <LogsPanel onClose={() => setSide("none")} />
              )}
              {side === "chats" && (
                <ChatListPage
                  threads={chatList.length ? chatList : listChatThreads()}
                  activeChatId={activeChatId}
                  onOpenChat={(t) => {
                    void openChatFullscreen(t);
                  }}
                  onReopenFile={(t) => void reopenChatFile(t)}
                  onClose={() => setSide("none")}
                />
              )}
            </div>
          </aside>
        )}
      </div>

      {(file?.kind === "docx" || file?.kind === "pdf") && (
        <FloatingSelectionToolbar
          state={selectionState}
          mode={file.kind === "pdf" ? "comment" : "full"}
          onFontFamily={(font) => {
            if (file.kind !== "docx") return;
            const text = selectionState.quote;
            if (!text) return;
            applyLocalEdit({ selectionText: text, fontFamily: font });
          }}
          onFontColor={(color) => {
            if (file.kind !== "docx") return;
            const text = selectionState.quote;
            if (!text) return;
            applyLocalEdit({ selectionText: text, fontColor: color });
          }}
          onTextAlign={(align) => {
            if (file.kind !== "docx") return;
            const text = selectionState.quote || "¶";
            applyLocalEdit({ selectionText: text, textAlign: align });
            const sel = window.getSelection();
            const p = sel?.anchorNode?.parentElement?.closest(
              ".nr-p"
            ) as HTMLElement | null;
            if (p) p.style.textAlign = align;
          }}
          onBold={() => {
            if (file.kind === "docx") execInline("bold");
          }}
          onItalic={() => {
            if (file.kind === "docx") execInline("italic");
          }}
          onUnderline={() => {
            if (file.kind === "docx") execInline("underline");
          }}
          onComment={() => {
            if (file.kind === "pdf") setPdfTextLayer(true);
            beginAddComment();
          }}
          onSendToAssistant={sendSelectionToAssistant}
        />
      )}

      {focusCommentId &&
        readerComments.some(
          (c) => !c.deleted && c.word_id === focusCommentId
        ) && (
          <CommentFocusSheet
            comments={readerComments}
            activeId={focusCommentId}
            onNavigate={(id) => {
              setFocusCommentId(id);
              setActiveCommentId(id);
            }}
            onClose={() => {
              setFocusCommentId(null);
              setActiveCommentId(null);
            }}
            onOpenAll={() => {
              setFocusCommentId(null);
              setActiveCommentId(null);
              setSide("comments");
            }}
            onReply={replyToComment}
            onDelete={deleteComment}
            onDeleteReply={deleteReply}
            onResolve={resolveComment}
            onResolveAll={resolveAllComments}
            onDeleteAll={deleteAllComments}
            onEdit={editComment}
            onEditReply={editReply}
          />
        )}

      {pdfReportExport && (
        <PdfReportSheet
          exported={pdfReportExport}
          apiBase={settings.nevisignApiBase}
          versionCode={pdfReportNativeVer?.versionCode ?? APP_VERSION_CODE}
          versionName={pdfReportNativeVer?.versionName ?? APP_VERSION_NAME}
          onClose={() => {
            setPdfReportExport(null);
            setPdfReportNativeVer(null);
          }}
        />
      )}

      {addCommentQuote !== null && (
        <AddCommentSheet
          quote={addCommentQuote}
          onSubmit={submitAddComment}
          onClose={() => {
            setAddCommentQuote(null);
            savedRangeRef.current = null;
            pendingPdfAnchorRef.current = null;
          }}
        />
      )}

      {printOpen && file && (
        <PrintSheet
          settings={settings.print}
          onChange={(partial) =>
            updateSettings({ print: { ...settings.print, ...partial } })
          }
          onPrint={() => void runPrint()}
          onClose={() => setPrintOpen(false)}
          busy={printBusy}
        />
      )}

      {(file || assistantOpen) && (
        <>
          {file &&
            !assistantOpen &&
            !selectionState.visible &&
            !addingComment && (
            <>
              {minimizedTool && !activeTool && (
                <MinimizedToolChip
                  label={toolLabel(minimizedTool)}
                  onOpen={restoreMinimizedTool}
                />
              )}
              <AssistantFab
                onClick={() => {
                  requireAuth(() => {
                    setAssistantScopedSelection(null);
                    setPendingQuote(null);
                    setFileMissingHint(null);
                    setAssistantOpen(true);
                    setAssistantFull(false);
                  });
                }}
              />
            </>
          )}
          <AssistantSheet
            open={assistantOpen}
            fullscreen={assistantFull}
            apiBase={settings.nevisignApiBase}
            documentTitle={file?.name}
            documentBytes={file?.buffer.byteLength}
            documentText={documentTextForAssistant}
            nevisignDocumentId={file?.nevisignDocumentId}
            scopedSelection={assistantScopedSelection}
            pendingQuote={pendingQuote}
            onPendingQuoteConsumed={() => setPendingQuote(null)}
            onClearScopedSelection={() => setAssistantScopedSelection(null)}
            editRootRef={docxContainerRef}
            activeChatId={activeChatId}
            onActiveChatIdChange={setActiveChatId}
            fileMissingHint={fileMissingHint}
            onClose={() => {
              setAssistantOpen(false);
              setAssistantFull(false);
              setAssistantScopedSelection(null);
              setFileMissingHint(null);
            }}
            onToggleFullscreen={() => setAssistantFull((f) => !f)}
            onOpenTools={() => {
              setToolsOpen(true);
            }}
          />
        </>
      )}

      {toolsOpen && (
        <ToolsMenu
          env={
            file?.kind === "pdf"
              ? "pdf"
              : file?.kind === "docx"
                ? "docx"
                : "home"
          }
          onSelect={onSelectTool}
          onClose={() => setToolsOpen(false)}
        />
      )}

      {activeTool === "summary" && (
        <SummaryToolPage
          title="خلاصه سند"
          text={summaryText}
          history={summaryHistory}
          busy={summaryBusy}
          regenerating={summaryRegenerating}
          error={summaryError}
          onRegenerate={() => void runSummaryTool()}
          onClose={() => {
            setActiveTool(null);
            setMinimizedTool(null);
          }}
          onMinimize={minimizeActiveTool}
          onPickHistory={(idx) => setSummaryText(summaryHistory[idx] ?? "")}
        />
      )}

      {activeTool === "legal-risk" && (
        <LegalRiskToolPage
          result={riskResult}
          busy={riskBusy}
          error={riskError}
          onClose={() => {
            setActiveTool(null);
            setMinimizedTool(null);
          }}
          onMinimize={minimizeActiveTool}
          onShare={() => {
            if (!riskResult) return;
            void shareText(
              legalRiskToMarkdown(riskResult),
              "nevisign-legal-risk.txt"
            );
          }}
        />
      )}

      {activeTool === "pdf-split" && (
        <PdfSplitPage
          initialBuffer={file?.kind === "pdf" ? file.buffer : null}
          initialName={file?.kind === "pdf" ? file.name : undefined}
          onClose={() => {
            setActiveTool(null);
            setMinimizedTool(null);
          }}
          onMinimize={minimizeActiveTool}
        />
      )}

      {activeTool === "pdf-merge" && (
        <PdfMergeSheet
          currentBuffer={file?.kind === "pdf" ? file.buffer : null}
          currentName={file?.kind === "pdf" ? file.name : undefined}
          onClose={() => {
            setActiveTool(null);
            setMinimizedTool(null);
          }}
          onMinimize={minimizeActiveTool}
        />
      )}

      {activeTool === "to-image" && file && (
        <ToImageSheet
          buffer={file.kind === "pdf" ? file.buffer : null}
          pageCount={
            file.kind === "pdf"
              ? pdfPageCount || 1
              : Math.max(1, docxPageCount)
          }
          kind={file.kind}
          docRoot={docxContainerRef.current}
          onClose={() => {
            setActiveTool(null);
            setMinimizedTool(null);
          }}
          onMinimize={minimizeActiveTool}
        />
      )}

      {activeTool === "compare" && (
        <TextComparePage
          apiBase={settings.nevisignApiBase}
          isAuthenticated={isAuthenticated}
          onRequestAuth={(retry) => requireAuth(retry)}
          initialPrimary={
            file
              ? {
                  name: file.name,
                  text:
                    documentTextForAssistant ||
                    annotations?.finalText ||
                    annotations?.originalText ||
                    "",
                }
              : null
          }
          onOpenResult={({ title, html, commentBodies }) => {
            openBlankSimpleDocument({ title, html });
            updateSettings({
              showTrackChanges: true,
              docxViewMode: "continuous",
            });
            setZoom(1);
            if (commentBodies.length) {
              const name = simpleDocFileName(title);
              const extras: ReaderComment[] = commentBodies.map((body, i) => ({
                word_id: `local-cmp-${Date.now()}-${i}`,
                author: "مقایسه متن",
                body,
                quote: body.slice(0, 80),
                date: new Date().toISOString(),
                replies: [],
              }));
              setReaderComments(extras);
              saveLocalComments(name, extras);
              setAnnotations((prev) =>
                prev
                  ? {
                      ...prev,
                      comments: extras,
                      counts: {
                        ...prev.counts,
                        comments: extras.length,
                      },
                    }
                  : prev,
              );
            }
          }}
          onClose={() => {
            setActiveTool(null);
            setMinimizedTool(null);
          }}
          onMinimize={minimizeActiveTool}
        />
      )}

      {revToast && (
        <RevToast message={revToast} onDone={() => setRevToast(null)} />
      )}

      {file ? (
        <SignatureActionSheet
          open={signatureActionOpen}
          fileName={file.name}
          busy={signatureActionBusy}
          error={signatureActionError}
          result={signatureActionResult}
          selfName={user?.fullName || "من"}
          selfPhone={user?.phone ?? null}
          onClose={() => {
            if (signatureActionBusy) return;
            setSignatureActionOpen(false);
          }}
          onSelfOnly={() => void startSelfSignature()}
          onSendToOthers={(input) => void startOthersSignature(input)}
        />
      ) : null}

      {signingSession && file?.nevisignDocumentId ? (
        <SigningOverlay
          apiBase={settings.nevisignApiBase}
          contractId={file.nevisignDocumentId}
          session={signingSession}
          mobileSheetOpen={signingSheetOpen}
          onMobileSheetOpenChange={setSigningSheetOpen}
          onSessionChange={setSigningSession}
          onComplete={() => {
            setSigningSession(null);
            setSigningSheetOpen(false);
            setKartablTask(null);
          }}
        />
      ) : null}

      {file?.nevisignDocumentId &&
      kartablTask &&
      (kartablTask.source === "contract_signature" ||
        kartablTask.source === "contract_approval") ? (
        <div className="kartabl-fab-bar">
          {kartablTask.source === "contract_signature" ? (
            <button
              type="button"
              className="kartabl-fab primary"
              onClick={() => {
                if (signingSession) {
                  setSigningSheetOpen(true);
                  return;
                }
                void detectPendingSignature(
                  settings.nevisignApiBase,
                  file.nevisignDocumentId!,
                ).then((pending) => {
                  setSigningSession(pending);
                  setSigningSheetOpen(Boolean(pending));
                  if (!pending) {
                    setRevToast("جلسه امضای فعالی برای شما یافت نشد");
                  }
                });
              }}
            >
              امضا
            </button>
          ) : (
            <button
              type="button"
              className="kartabl-fab primary"
              disabled={!kartablTask.sourceRef?.processId}
              onClick={() => setApprovalOpen(true)}
            >
              تأیید / عدم تأیید
            </button>
          )}
        </div>
      ) : null}

      {approvalOpen &&
      kartablTask?.contractId &&
      kartablTask.sourceRef?.processId ? (
        <ApprovalActSheet
          open={approvalOpen}
          apiBase={settings.nevisignApiBase}
          contractId={kartablTask.contractId}
          processId={kartablTask.sourceRef.processId}
          taskTitle={kartablTask.title}
          onClose={() => setApprovalOpen(false)}
          onSuccess={() => {
            setRevToast("وضعیت تأیید ثبت شد");
            setKartablTask(null);
            goHome();
          }}
        />
      ) : null}

      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} />

      <SimpleDocSaveModal
        open={simpleSaveOpen && blankSimpleMode}
        initialTitle={(file?.name || "سند بدون عنوان").replace(/\.docx$/i, "")}
        busy={simpleSaveBusy}
        error={simpleSaveError}
        onClose={() => {
          if (simpleSaveBusy) return;
          setSimpleSaveOpen(false);
          setSimpleSaveError(null);
        }}
        onConfirm={(opts) => {
          void confirmSimpleDocSave(opts);
        }}
      />
    </div>
  );
}
